import { Download, Share } from "lucide-react";
import { useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

// The browser fires this once, early, so it is caught at startup rather than in the page.
let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

export function captureInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    notify();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;
const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

export function InstallApp() {
  const prompt = useSyncExternalStore(subscribe, () => deferred);
  if (isStandalone()) return null;

  if (prompt) {
    return (
      <section className="mt-12">
        <h2 className="text-base">Приложение на экране</h2>
        <p className="mt-1 text-sm text-muted">
          Значок «Пропуска» рядом с другими приложениями — открывается без адресной строки.
        </p>
        <Button
          variant="secondary"
          className="mt-4"
          onClick={async () => {
            await prompt.prompt();
            await prompt.userChoice;
            deferred = null;
            notify();
          }}
        >
          <Download aria-hidden />
          Установить на экран
        </Button>
      </section>
    );
  }
  if (isIos()) {
    return (
      <section className="mt-12">
        <h2 className="text-base">Приложение на экране</h2>
        <p className="mt-1 text-sm text-muted">
          В Safari нажмите{" "}
          <Share aria-label="Поделиться" className="inline size-4 align-text-bottom" />{" "}
          «Поделиться», затем «На экран „Домой“».
        </p>
      </section>
    );
  }
  return null;
}
